import { useRef, useState } from "react";
import { C } from "../../shared/theme";
import { EditorFrame, fieldLabel as label, secondaryButton, DeleteButton } from "../../shared/EditorFrame";
import { PLAN_ITEM_KINDS } from "./planItems";
import { resizeImage } from "./resizeImage";

// Adding or editing one dream, over the page (the shared EditorFrame).
// `item` is null for a new one. onSave(fields) and onDelete() return promises.
export function PlanItemEditor({ kind, item, onSave, onDelete, onClose }) {
  const k = PLAN_ITEM_KINDS[kind];
  const [title, setTitle] = useState(item?.title || "");
  const [image, setImage] = useState(item?.image || null);
  const [busy, setBusy] = useState(null); // "photo" | "save" | "delete"
  const [error, setError] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const fileRef = useRef(null);
  const canSave = title.trim() && !busy;

  const pickPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy("photo");
    setError(null);
    try { setImage(await resizeImage(file)); }
    catch (err) { setError(err.message); }
    finally { setBusy(null); }
  };

  const save = async () => {
    if (!canSave) return;
    setBusy("save");
    setError(null);
    try {
      await onSave({ title: title.trim(), image });
      onClose();
    } catch (err) {
      console.error(`Saving a ${k.one} failed:`, err);
      setError(`We couldn't save your ${k.one}. Check your connection and try again.`);
      setBusy(null);
    }
  };

  const remove = async () => {
    if (!confirmDelete) { setConfirmDelete(true); return; }
    setBusy("delete");
    try { await onDelete(); onClose(); }
    catch (err) {
      console.error(`Deleting a ${k.one} failed:`, err);
      setError(`We couldn't delete your ${k.one}. Check your connection and try again.`);
      setBusy(null);
    }
  };

  return (
    <EditorFrame id="plan-item-editor-title" title={item ? `Edit ${k.one}` : `New ${k.one}`}
      canSave={canSave} saving={busy === "save"} busy={!!busy} onSave={save} onClose={onClose}>
      <div>
        <label htmlFor="plan-item-title" style={{ ...label(), display: "block" }}>{k.prompt}</label>
        <textarea id="plan-item-title" value={title} onChange={e => setTitle(e.target.value)} rows={3} autoFocus={!item}
          style={{ width: "100%", background: C.surface2, border: `1px solid ${C.border}`, borderRadius: C.rsm, padding: "12px 14px", fontSize: 16, color: C.text, fontFamily: "inherit", resize: "vertical", boxSizing: "border-box", lineHeight: 1.5 }} />
      </div>

      <div>
        <div style={label()}>Photo</div>
        {image && (
          <img src={image} alt={title.trim() || `Your ${k.one}`}
            style={{ display: "block", width: "100%", maxHeight: 360, objectFit: "cover", borderRadius: C.r, marginBottom: 12 }} />
        )}
        <input ref={fileRef} type="file" accept="image/*" onChange={pickPhoto} style={{ display: "none" }} />
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button onClick={() => fileRef.current?.click()} disabled={!!busy} style={secondaryButton()}>
            {busy === "photo" ? "Preparing photo…" : image ? "Change photo" : "Add a photo"}
          </button>
          {image && <button onClick={() => setImage(null)} disabled={!!busy} style={secondaryButton()}>Remove photo</button>}
        </div>
      </div>

      {error && <p role="alert" style={{ margin: 0, fontSize: 15, lineHeight: 1.5, color: C.red }}>{error}</p>}

      {item && <DeleteButton noun={k.one} confirming={confirmDelete} deleting={busy === "delete"} disabled={!!busy} onClick={remove} />}
    </EditorFrame>
  );
}
